import {
  BodyText,
  Button,
  Container,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
} from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { useDeleteOTCPrescription } from "../network/useDeleteOTCPrescription";
import type { OTCPrescription } from "../network/useOTCPrescriptions";
import { useUpdateOTCPrescription } from "../network/useUpdateOTCPrescription";
import { otcPrescriptionSchema } from "./schema";

export const OTCMedication = ({
  patientId,
  encounterId,
  carePlanId,
  otcPrescription,
  readOnly,
}: {
  patientId: string;
  encounterId: string;
  carePlanId: string;
  otcPrescription: OTCPrescription;
  readOnly?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const { mutateAsync: deleteOTCPrescription, isPending: isDeleting } =
    useDeleteOTCPrescription({
      patientId,
      encounterId,
      carePlanId,
      carePlanActivityId: otcPrescription.activityId,
    });

  const { mutateAsync: updateOTCPrescription, isPending: isUpdating } =
    useUpdateOTCPrescription({
      patientId,
      encounterId,
      carePlanId,
      carePlanActivityId: otcPrescription.activityId,
    });

  const form = useForm<z.infer<typeof otcPrescriptionSchema>>({
    resolver: zodResolver(otcPrescriptionSchema),
    defaultValues: {
      details: otcPrescription.details,
    },
  });

  const onSubmit = async (data: z.infer<typeof otcPrescriptionSchema>) => {
    await updateOTCPrescription({
      freetextMedicationDetails: data.details,
    });
    setIsEditing(false);
  };

  const onDelete = () => {
    deleteOTCPrescription();
  };

  const onCancel = () => {
    setIsEditing(false);
    form.reset({
      details: otcPrescription.details,
    });
  };

  return (
    <Container className="bg-secondary border-primary p-3 border gap-2 flex flex-col rounded-md">
      {isEditing ? (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-2"
          >
            <FormField
              control={form.control}
              name="details"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} className="w-full" />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex flex-1 justify-end gap-2">
              <Button
                variant="danger"
                size="xs"
                onClick={onDelete}
                className="w-fit"
                isLoading={isDeleting}
              >
                Delete
              </Button>
              <div className="flex gap-2 flex-1 justify-end">
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={onCancel}
                  className="w-fit"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="xs"
                  type="submit"
                  className="w-fit"
                  disabled={!form.formState.isDirty}
                  isLoading={isUpdating}
                >
                  {form.formState.isDirty ? "Save" : "Saved"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      ) : (
        <div className="flex-1 flex flex-row items-center gap-2">
          <BodyText textColor="link" size="sm" bold className="flex-1">
            {otcPrescription.details}
          </BodyText>
          <Button
            variant="icon"
            size="sm"
            className="p-0"
            onClick={() => setIsEditing(true)}
            disabled={readOnly}
          >
            <Pencil size={16} className="stroke-link" />
          </Button>
        </div>
      )}
    </Container>
  );
};
